import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { fetchPredictive } from '../services/analyticsService';
import { novaGenerateReport, novaDownloadReport } from '../services/novaService';
import { NovaQuickAnalysisBarPredictive } from '../components/shared/NovaQuickAnalysis';
import ReportLanguageToggle, { useReportLanguage } from '../components/shared/ReportLanguageToggle';
import './Suppliers.css';

const TABS = [
  { key: 'expected', label: 'Expected' },
  { key: 'delayed', label: 'Delayed' },
  { key: 'other', label: 'Other' },
];

const STATUS_BADGE = {
  EXPECTED: 'teal-soft',
  DELAYED: 'amber',
  IN_PROGRESS: 'blue',
  UNKNOWN: '',
};

const CHART_CARD = {
  background: 'white',
  borderRadius: 14,
  border: '1px solid #e3e8ef',
  padding: '14px 18px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 10,
};

const DIST_COLORS = {
  expected: '#0D5C4F',
  delayed: '#B7791F',
  other: '#1E6BA8',
};

/* ── Distribution bar ── */
function DistributionBar({ summary }) {
  if (!summary) return null;
  const total = summary.expected + summary.delayed + summary.inProgress + summary.unknown;
  if (total === 0) return null;
  const segments = [
    { key: 'expected', label: 'Expected', val: summary.expected, color: DIST_COLORS.expected },
    { key: 'delayed', label: 'Delayed', val: summary.delayed, color: DIST_COLORS.delayed },
    { key: 'other', label: 'Other', val: summary.inProgress + summary.unknown, color: DIST_COLORS.other },
  ];
  return (
    <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e3e8ef', padding: '14px 18px', marginBottom: 16 }}>
      <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>Distribution</div>
      <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: '#f3f4f6' }}>
        {segments.map(s => s.val > 0 && (
          <div key={s.key} title={`${s.label}: ${s.val}`} style={{ width: `${(s.val / total) * 100}%`, background: s.color, transition: 'width .3s' }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 11.5, color: 'var(--navy)' }}>
        {segments.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            {s.label} ({s.val})
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PredictiveSupplierDevelopments() {
  const { instance: msalInstance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('expected');
  const [search, setSearch] = useState(() => {
    const p = new URLSearchParams(location.search);
    return p.get('supplier') || '';
  });
  const [exportingPptx, setExportingPptx] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [exportErr, setExportErr] = useState('');
  const { lang: reportLang, setLang: setReportLang } = useReportLanguage();

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    const params = {};
    if (search.trim()) params.search = search.trim();
    fetchPredictive(msalInstance, params)
      .then(d => setData(d))
      .catch(() => setErr('Failed to load predictive data'))
      .finally(() => setLoading(false));
  }, [search, msalInstance]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Sync URL ↔ search (supplier filter)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (search.trim()) {
      if (p.get('supplier') !== search.trim()) navigate(`?supplier=${encodeURIComponent(search.trim())}`, { replace: true });
    } else if (p.has('supplier')) {
      navigate(location.pathname, { replace: true });
    }
  }, [search, location.search, location.pathname, navigate]);

  const scopeToSupplier = useCallback((supplier) => {
    setSearch(supplier);
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    if (tab === 'expected') return data.expected || [];
    if (tab === 'delayed') return data.delayed || [];
    return data.other || [];
  }, [data, tab]);

  const summary = data?.summary;

  const handleExport = useCallback(async (format) => {
    const setLoading = format === 'pptx' ? setExportingPptx : setExportingXlsx;
    setLoading(true); setExportErr('');
    try {
      const filters = {};
      if (tab) filters.status = tab;
      const meta = await novaGenerateReport(msalInstance, { reportType: 'predictive', format, filters, language: reportLang });
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `predictive-report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setExportErr('Export failed. Please try again.'); }
    finally { setLoading(false); }
  }, [msalInstance, tab, reportLang]);

  return (
    <div className="sup-page">
      <div className="mb-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>Predictive supplier developments</h2>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Analytics</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {exportErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{exportErr}</span>}
          <ReportLanguageToggle value={reportLang} onChange={setReportLang} compact />
          <button type="button" className="sup-badge teal-soft" disabled={exportingPptx} onClick={() => handleExport('pptx')} style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
            cursor: exportingPptx ? 'wait' : 'pointer', background: exportingPptx ? '#e8e8e8' : undefined,
          }}>
            {exportingPptx ? 'Generating...' : 'Download PowerPoint'}
          </button>
          <button type="button" className="sup-badge teal-soft" disabled={exportingXlsx} onClick={() => handleExport('xlsx')} style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #156082',
            cursor: exportingXlsx ? 'wait' : 'pointer', background: exportingXlsx ? '#e8e8e8' : undefined,
          }}>
            {exportingXlsx ? 'Generating...' : 'Download Excel'}
          </button>
        </div>
      </div>

      <NovaQuickAnalysisBarPredictive
        placeholder="Quick predictive analysis — type a supplier or molecule..."
        supplierList={(() => { if (!data) return []; const s = new Set(); for (const arr of [data.expected || [], data.delayed || [], data.other || []]) for (const r of arr) if (r.supplierName) s.add(r.supplierName); return [...s].sort(); })()}
        moleculeList={(() => { if (!data) return []; const s = new Set(); for (const arr of [data.expected || [], data.delayed || [], data.other || []]) for (const r of arr) if (r.inn) s.add(r.inn); return [...s].sort(); })()}
        onSubmit={(entity) => setSearch(entity)}
      />

      {/* KPI stat cards */}
      {summary && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { label: 'Expected', val: summary.expected, cls: 'teal-soft' },
            { label: 'Delayed', val: summary.delayed, cls: 'amber' },
            { label: 'In Progress', val: summary.inProgress, cls: 'blue' },
            { label: 'Unknown', val: summary.unknown, cls: '' },
          ].map(s => (
            <div key={s.label} className="sup-kpi" style={{ flex: '1 1 140px', maxWidth: 200 }}>
              <div className="sup-kpi-lbl">{s.label}</div>
              <div className="sup-kpi-val">{s.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Distribution bar accent */}
      <DistributionBar summary={summary} />

      {/* Nova Insight */}
      {data?.novaSummary && (
        <div style={{ ...CHART_CARD, marginBottom: 16 }}>
          <span className="ai-badge" style={{ flexShrink: 0, marginTop: 1 }}>✦ Nova</span>
          <div style={{ fontSize: 13, color: 'var(--navy)', lineHeight: 1.55 }}>{data.novaSummary}</div>
        </div>
      )}

      {/* Filters + tabs */}
      <div className="sup-filters-row" style={{ alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                border: tab === t.key ? '1px solid var(--teal)' : '1px solid var(--brd)',
                background: tab === t.key ? 'var(--teal3)' : 'var(--card)',
                color: tab === t.key ? 'var(--teal)' : 'var(--navy)',
              }}
            >
              {t.label}
              {summary && <span style={{ marginLeft: 4, opacity: 0.7 }}>
                ({t.key === 'expected' ? summary.expected : t.key === 'delayed' ? summary.delayed : (summary.inProgress + summary.unknown)})
              </span>}
            </button>
          ))}
        </div>
        <div>
          <input
            className="sup-input"
            style={{ width: 220 }}
            placeholder="Search INN or supplier..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {search && (
          <button type="button" className="sup-clear-link" onClick={() => setSearch('')}>
            Clear
          </button>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Loading...
        </div>
      )}

      {err && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--red)' }}>{err}</p>
        </div>
      )}

      {!loading && !err && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll">
            <table className="sup-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Molecule (INN)</th>
                  <th>Form</th>
                  <th>Strength</th>
                  <th>ATC</th>
                  <th>Target Quarter</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: 'var(--t3)' }}>No items in this category.</td></tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={i} role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                      onClick={() => scopeToSupplier(r.supplierName)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scopeToSupplier(r.supplierName); } }}
                    >
                      <td style={{ fontWeight: 600 }}>{r.supplierName || '—'}</td>
                      <td className="sup-inn">{r.inn || '—'}</td>
                      <td>{r.dosageForm || '—'}</td>
                      <td>{r.strength || '—'}</td>
                      <td>{r.atcCode || '—'}</td>
                      <td>{r.targetQuarterLabel || '—'}</td>
                      <td>
                        <span className={`sup-badge ${STATUS_BADGE[r.status] || ''}`}>
                          {r.status === 'DELAYED' && r.quartersLate
                            ? `Delayed (${r.quartersLate}Q late)`
                            : r.status === 'IN_PROGRESS' ? 'In Progress'
                            : r.status === 'UNKNOWN' ? 'Unknown'
                            : r.status || '—'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {rows.length > 0 && (
            <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--t3)', borderTop: '1px solid #eef3f7' }}>
              {rows.length} item{rows.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 12, fontStyle: 'italic' }}>
        Projections from supplier-provided planned dates. Read-only, not stored.
      </div>
    </div>
  );
}
